import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { ContractTaxonomyFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import TextArea from 'modules/core/components/Inputs/TextArea'
import Modal from 'modules/core/components/Modal'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'
import type SupplementForm from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/SupplementForm'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import type SupplementObservationForm from 'modules/payroll_bundle/lib/SupplementObservationForm'
import { useStatusAuthorizations } from 'modules/payroll_bundle/lib/statusAuthorizationsContext'
import SupplementObservation from 'modules/payroll_bundle/store/models/SupplementObservation'

interface Props {
  onClose: () => void
  form: SupplementObservationForm
  employee: Employee | undefined
  supplementForm: SupplementForm
  onSupplementFormSubmit: () => Promise<unknown>
  store: SupplementsFormStore
  taxonomies: ContractTaxonomyFragment[]
}

const SupplementObservationModal = ({
  onClose,
  form,
  employee,
  supplementForm,
  onSupplementFormSubmit,
  store,
  taxonomies,
}: Props) => {
  const [disabled, setDisabled] = React.useState(false)

  const { supplementPermissions } = useStatusAuthorizations()

  const onSave = async () => {
    setDisabled(true)

    if (form.get('supplement_id').value && !form.get('text').value) {
      await deleteSupplement()
      onClose()

      return
    }

    if (
      !supplementForm.get('id').value &&
      !!supplementForm.get('contracts_compensation_id').value
    ) {
      await saveSupplement()
    }

    const supplementId = form.data().supplement_id

    if (!supplementId) {
      form.get('supplement_id').set(supplementForm.get('id').value)
    }

    const formData = form.supplementObservationData
    const observation = new SupplementObservation(formData)
    await asyncAction(() => form.save(observation), {
      successFlash: true,
    })
    setDisabled(false)
    onClose()
  }

  const deleteSupplement = async () => {
    const formData = form.supplementObservationData
    const observation = new SupplementObservation(formData)
    await observation.destroy()
    store.removeSupplementObservationForm(formData.id)
  }

  const saveSupplement = async () => {
    if (!supplementForm.get('amount_in_cents').value) {
      supplementForm.get('amount_in_cents').set('0')
    }

    await onSupplementFormSubmit()
  }

  const closeModal = () => {
    form.get('text').set(form.get('text').originalValue)
    onClose()
  }

  const employeeId = employee?.attributes.id
  const canEditObservations =
    employeeId && supplementPermissions[String(employeeId)]?.canEditEmployeeObservation === true

  const getTaxonomyName = () => {
    const taxonomyId = supplementForm.data().contracts_taxonomy_id

    if (!taxonomyId) return ''

    return taxonomies.find(({ id }) => taxonomyId == id)?.name
  }

  const readOnly = disabled || !canEditObservations

  return (
    <Modal
      title={i18n.t('payroll_bundle.supplements.supplement_observations_modal.title')}
      description={i18n.t('payroll_bundle.supplements.supplement_observations_modal.description', {
        taxonomyName: getTaxonomyName(),
        fullName: employee && users.get(employee.attributes.access_id)?.fullName(),
      })}
      illustration="document-edit"
      onClose={closeModal}
      footer={
        <Button onClick={onSave} type="primary" disabled={readOnly}>
          {i18n.t('payroll_bundle.supplements.supplement_observations_modal.submit')}
        </Button>
      }
    >
      <TextArea
        mandatory={false}
        readonly={readOnly}
        placeholder={i18n.t('payroll_bundle.supplements.supplement_observations_modal.placeholder')}
        field={form.get('text')}
      />
    </Modal>
  )
}

export default observer(SupplementObservationModal)
